import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useListenAvatarUpdate } from '../events/avatarUpdateEvent';

interface AvatarContextData {
  avatarUrl: string | null;
  setAvatarUrl: (url: string | null) => void;
  updateAvatar: (newUrl: string) => void;
  updateAvatarUrl: (newUrl: string) => void;
  refreshAvatar: () => void;
  isLoading: boolean;
}

interface AvatarProviderProps {
  children: ReactNode;
}

const AvatarContext = createContext<AvatarContextData>({} as AvatarContextData);

export const AvatarProvider: React.FC<AvatarProviderProps> = ({ children }) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const previousAvatarRef = useRef<string | null>(null);

  // Função para buscar o avatar atual do usuário
  const fetchUserAvatar = () => {
    if (user) {
      // Verificar se o usuário tem um avatar definido
      // Primeiro tentamos obter do objeto user (várias propriedades possíveis)
      let userAvatarUrl = user.user_metadata?.avatar_url ||
           // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (user as any).avatarUrl ||
        user.user_metadata?.avatarUrl ||
        null;

      // Se não encontrou no objeto user, tentamos obter do localStorage (cache)
      if (!userAvatarUrl) {
        const cachedAvatarUrl = localStorage.getItem('cached_avatar_url');
        if (cachedAvatarUrl) {
          userAvatarUrl = cachedAvatarUrl;
        }
      }

      // Se ainda não temos avatar, mas tínhamos um anterior, preservamos o anterior
      if (!userAvatarUrl && previousAvatarRef.current) {
        userAvatarUrl = previousAvatarRef.current;

        // Salvar o avatar preservado no localStorage para garantir persistência
        localStorage.setItem('cached_avatar_url', userAvatarUrl);
        localStorage.setItem('avatar_timestamp', Date.now().toString());
      }

      // Atualizar a referência do avatar anterior se temos um novo avatar
      if (userAvatarUrl) {
        previousAvatarRef.current = userAvatarUrl;
      }

      setAvatarUrl(userAvatarUrl);
    } else {
      // Quando o usuário faz logout, limpar tudo
      setAvatarUrl(null);
      previousAvatarRef.current = null;
      localStorage.removeItem('cached_avatar_url');
      localStorage.removeItem('avatar_timestamp');
    }
  };

  useEffect(() => {
    // Buscar o avatar quando o usuário mudar
    fetchUserAvatar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateAvatar = useCallback((newUrl: string) => {
    // URL vazia = avatar removido. Limpa cache e ref pra não ressuscitar o avatar
    // antigo via fetchUserAvatar (que preserva previousAvatarRef/localStorage).
    if (!newUrl) {
      setAvatarUrl(null);
      previousAvatarRef.current = null;
      localStorage.removeItem('cached_avatar_url');
      localStorage.removeItem('avatar_timestamp');
      return;
    }
    setAvatarUrl(newUrl);
    // Atualizar a referência anterior para preservar o novo avatar
    previousAvatarRef.current = newUrl;
    // Salvar no localStorage para persistência
    localStorage.setItem('cached_avatar_url', newUrl);
    localStorage.setItem('avatar_timestamp', Date.now().toString());
  }, []);

  // Reflete na navbar/UI o avatar recém-enviado (com cache-buster) sem esperar o
  // próximo refreshSession recompor o objeto `user`.
  useListenAvatarUpdate(user?.id, (data) => updateAvatar(data.avatarUrl));

  // Alias para updateAvatar para compatibilidade com componentes existentes
  const updateAvatarUrl = (newUrl: string) => {
    updateAvatar(newUrl);
  };

  // Função para forçar a atualização do avatar
  const refreshAvatar = () => {
    setIsLoading(true);
    fetchUserAvatar();
    setIsLoading(false);
  };

  return (
    <AvatarContext.Provider
      value={{
        avatarUrl,
        setAvatarUrl,
        updateAvatar,
        updateAvatarUrl,
        refreshAvatar,
        isLoading
      }}
    >
      {children}
    </AvatarContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAvatar = () => {
  const context = useContext(AvatarContext);

  if (!context) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }

  return context;
};
